import type { Lang } from '../domain/types';

export const nowISO = (): string => new Date().toISOString();

export function daysAgoISO(days: number, hours = 0): string {
  return new Date(Date.now() - days * 86_400_000 - hours * 3_600_000).toISOString();
}

export function daysFromNowISO(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toISOString();
}

/** "2 days ago" / "לפני יומיים" style label. */
export function relativeTime(iso: string, lang: Lang, now = Date.now()): string {
  const diff = now - new Date(iso).getTime();
  const min = Math.round(diff / 60_000);
  const he = lang === 'he';
  if (min < 1) return he ? 'הרגע' : 'just now';
  if (min < 60) return he ? `לפני ${min} דק׳` : `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return he ? (h === 1 ? 'לפני שעה' : `לפני ${h} שעות`) : h === 1 ? '1 hour ago' : `${h} hours ago`;
  const d = Math.round(h / 24);
  if (d < 30) return he ? (d === 1 ? 'אתמול' : d === 2 ? 'לפני יומיים' : `לפני ${d} ימים`) : d === 1 ? 'yesterday' : `${d} days ago`;
  const mo = Math.round(d / 30);
  if (mo < 12) return he ? (mo === 1 ? 'לפני חודש' : `לפני ${mo} חודשים`) : mo === 1 ? '1 month ago' : `${mo} months ago`;
  const y = Math.round(mo / 12);
  return he ? (y === 1 ? 'לפני שנה' : `לפני ${y} שנים`) : y === 1 ? '1 year ago' : `${y} years ago`;
}

/*
 * A date formatter is one of the dearest small things a browser makes — it
 * loads a locale's whole calendar to be built — and one that is built to write
 * a single "14" and thrown away is nearly all cost. The calendar used to build
 * four per card per render: with a large family on the line that was a third of
 * a second of formatters every time anything on the screen changed. Here each
 * distinct (locale, options) is built once and kept.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();

export function dateFormatter(lang: Lang, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${lang}|${JSON.stringify(options)}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(lang === 'he' ? 'he-IL' : 'en-GB', options);
    formatters.set(key, f);
  }
  return f;
}

/** A calendar day (YYYY-MM-DD) written out in the reader's language; noon, so the answer is the same in every time zone. */
export function formatDay(iso: string, lang: Lang, options: Intl.DateTimeFormatOptions): string {
  try {
    return dateFormatter(lang, options).format(new Date(`${iso}T12:00:00`));
  } catch {
    return iso;
  }
}

export function formatDateTime(iso: string, lang: Lang): string {
  try {
    return new Intl.DateTimeFormat(lang === 'he' ? 'he-IL' : 'en-CA', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** "21:40" / "9:40 pm" in a time zone (undefined when the zone is unknown or invalid). */
export function formatLocalTime(at: Date, timezone: string | undefined, lang: Lang): string | undefined {
  if (!timezone) return undefined;
  try {
    return new Intl.DateTimeFormat(lang === 'he' ? 'he-IL' : 'en-GB', { timeZone: timezone, hour: '2-digit', minute: '2-digit' }).format(at);
  } catch {
    return undefined;
  }
}

/** Whether two zones show the same wall-clock time right now (then a "local time there" line is noise). */
export function sameWallClock(at: Date, a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  try {
    const f = (tz: string) => new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit' }).format(at);
    return f(a) === f(b);
  } catch {
    return false;
  }
}
